/**
 * Profile and workspace-settings field rules shared by the client (instant validation) and the server (the
 * authoritative check before the database CHECKs): registry identity-workspace.profile.name-username,
 * identity-workspace.profile.avatar and identity-workspace.workspace.general-settings; ADR-0040 decision 16.
 *
 * Usernames: Unicode letters and digits plus `.`, `_` and `-`, 1–40 characters, unique per workspace
 * case-insensitively (citext); the DB CHECK also rejects whitespace, control characters, `@`, `/` and `\`, and the
 * `invited-` prefix (reserved for pending invitees, visibility spec D7).
 */
import { z } from 'zod';
import { urlKeyProblem } from '../url-key';

export const FULL_NAME_MAX = 100;
export const USERNAME_MAX = 40;
export const WORKSPACE_NAME_MAX = 100;

export const USERNAME_RE = /^[\p{L}\p{N}._-]{1,40}$/u;

export const MESSAGES = {
  nameRequired: 'Full name is required.',
  nameTooLong: `Full name can be at most ${FULL_NAME_MAX} characters.`,
  usernameRequired: 'Username is required.',
  usernameTooLong: `Username can be at most ${USERNAME_MAX} characters.`,
  usernameChars: 'Usernames can contain only letters, numbers, periods (.), hyphens (-) and underscores (_).',
  usernameReserved: 'That username is reserved.',
  usernameTaken: 'That username is already taken.',
  workspaceNameRequired: 'Workspace name is required.',
  workspaceNameTooLong: `Workspace name can be at most ${WORKSPACE_NAME_MAX} characters.`,
  urlKeyInvalid: 'URLs use 2–48 lowercase letters, numbers and hyphens, and can’t start with a hyphen.',
  urlKeyReserved: 'That URL is reserved.',
  urlKeyTaken: 'That URL isn’t available.',
} as const;

/** Count user-perceived characters the way the DB's char_length counts code points. */
function codePoints(s: string): number {
  return [...s].length;
}

export const zFullName = z
  .string()
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, MESSAGES.nameRequired)
  .refine((s) => codePoints(s) <= FULL_NAME_MAX, MESSAGES.nameTooLong);

export const zUsername = z
  .string()
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, MESSAGES.usernameRequired)
  .refine((s) => codePoints(s) <= USERNAME_MAX, MESSAGES.usernameTooLong)
  .refine((s) => USERNAME_RE.test(s), MESSAGES.usernameChars)
  .refine((s) => !s.toLowerCase().startsWith('invited-'), MESSAGES.usernameReserved);

export const zWorkspaceName = z
  .string()
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, MESSAGES.workspaceNameRequired)
  .refine((s) => codePoints(s) <= WORKSPACE_NAME_MAX, MESSAGES.workspaceNameTooLong);

/** The workspace URL key: the shared rule (`../url-key`, W1 step 0 C8) — format, then the reserved words. */
export const zUrlKey = z
  .string()
  .transform((s) => s.trim().toLowerCase())
  .refine((s) => urlKeyProblem(s) !== 'invalid', MESSAGES.urlKeyInvalid)
  .refine((s) => urlKeyProblem(s) !== 'reserved', MESSAGES.urlKeyReserved);

/** 0 = January … 11 = December (workspace.fiscal_year_start_month). */
export const zFiscalMonth = z.number().int().min(0).max(11);

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** First problem with a value, or null (for inline field errors). */
export function firstIssue(schema: z.ZodType, value: unknown): string | null {
  const r = schema.safeParse(value);
  return r.success ? null : (r.error.issues[0]?.message ?? 'Invalid value.');
}

// ---------------------------------------------------------------------------------------------------------------
// initials and avatar colors (identical on every client and in email)

type SegmenterLike = { segment(input: string): Iterable<{ segment: string }> };

function graphemes(s: string): string[] {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => SegmenterLike }).Segmenter;
  if (Seg) return Array.from(new Seg(undefined, { granularity: 'grapheme' }).segment(s), (x) => x.segment);
  return [...s];
}

const LETTER_OR_DIGIT = /^[\p{L}\p{N}]/u;

/**
 * First and last initials: 'Bo Lindqvist' → 'BL', 'Łukasz Żak' → 'ŁŻ', 'Ada' → 'A'. A name that starts with
 * something other than a letter or digit (an emoji) shows only that first grapheme: '🚀 Rocket' → '🚀'.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  if (words.length === 0) return '?';
  const first = graphemes(words[0]!)[0] ?? '?';
  if (!LETTER_OR_DIGIT.test(first)) return first;
  const last = words.length > 1 ? (graphemes(words.at(-1)!)[0] ?? '') : '';
  const second = LETTER_OR_DIGIT.test(last) ? last : '';
  return (first + second).toLocaleUpperCase();
}

/**
 * Avatar tile fills: white initials reach ≥ 4.5:1 on each in both themes (the @quadratic/ui AVATAR_PALETTE
 * contrast test covers the first eight; the rest were checked the same way: each is ≥ 4.5:1 against #FFFFFF).
 */
export const AVATAR_COLORS = [
  '#4A5BC4',
  '#1F7A5E',
  '#A4532A',
  '#7A48B8',
  '#2B6E8F',
  '#A23E66',
  '#5A6B22',
  '#8A5A1E',
  '#B03A2E',
  '#2F6B3C',
  '#5B4BA8',
  '#0F6E77',
  '#9A3F8C',
  '#6B5B2E',
] as const;

/** The DB default for workspace_user.avatar_color: "not chosen", so the tile color comes from the id. */
export const DEFAULT_AVATAR_COLOR = '#6B7280';

function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** The tile color of a user (or workspace): a stored non-default color wins, else a stable pick by id. */
export function avatarColorOf(id: string, stored?: string | null): string {
  if (stored && /^#[0-9a-f]{6}$/i.test(stored) && stored.toUpperCase() !== DEFAULT_AVATAR_COLOR) return stored;
  return AVATAR_COLORS[fnv1a(id) % AVATAR_COLORS.length]!;
}

// ---------------------------------------------------------------------------------------------------------------
// away status (registry users.hover-card AC3)

/** Away when a status label or emoji is set and its end is in the future or absent. */
export function isAway(u: { statusLabel?: unknown; statusEmoji?: unknown; statusUntilAt?: unknown }, now: number): boolean {
  const set = (typeof u.statusLabel === 'string' && u.statusLabel !== '') || (typeof u.statusEmoji === 'string' && u.statusEmoji !== '');
  if (!set) return false;
  return typeof u.statusUntilAt !== 'number' || u.statusUntilAt > now;
}
